<script setup>
import { onMounted, ref } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"
import VisorSoporte from "@/components/rrhh/VisorSoporte.vue"

const { hasRole } = useAuth()

const LABELS_TIPO = {
  vacaciones: "Vacaciones",
  permiso: "Permiso",
  incapacidad: "Incapacidad",
  cita_medica: "Cita médica",
  otro: "Otro"
}

const ESTADO_BADGE = {
  pendiente: "bg-warning text-dark",
  aprobado: "bg-success",
  rechazado: "bg-danger",
  cancelado: "bg-secondary"
}

const ausencias = ref([])
const cargando = ref(true)
const filtroEstado = ref("pendiente")
const soporteAbierto = ref(null)

async function cargar() {
  cargando.value = true
  try {
    const params = {}
    if (filtroEstado.value) params.estado = filtroEstado.value

    const { data } = await api.get("/rrhh/ausencias/equipo", { params })
    ausencias.value = data.ausencias
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la bandeja de aprobaciones",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(cargar)

function formatDate(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

async function resolver(ausencia, estado) {
  const { value: comentario, isConfirmed } = await Swal.fire({
    icon: estado === "aprobado" ? "question" : "warning",
    title: estado === "aprobado" ? "¿Aprobar solicitud?" : "¿Rechazar solicitud?",
    text: `${ausencia.nombres} ${ausencia.apellidos} - ${LABELS_TIPO[ausencia.tipo] || ausencia.tipo}`,
    input: "textarea",
    inputLabel: "Comentario (opcional)",
    showCancelButton: true,
    confirmButtonText: estado === "aprobado" ? "Sí, aprobar" : "Sí, rechazar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: estado === "aprobado" ? "#2dce89" : "#f5365c"
  })
  if (!isConfirmed) return

  try {
    await api.put(`/rrhh/ausencias/${ausencia.id}/resolver`, { estado, comentario })
    await cargar()

    Swal.fire({
      icon: "success",
      title: estado === "aprobado" ? "Solicitud aprobada" : "Solicitud rechazada",
      timer: 1600,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo resolver la solicitud",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0"><i class="ni ni-check-bold text-primary me-2"></i>Aprobaciones</h5>
        <div class="d-flex gap-2 align-items-end">
          <div>
            <label class="form-label text-sm mb-1">Estado</label>
            <select class="form-select form-select-sm" v-model="filtroEstado" @change="cargar">
              <option value="pendiente">Pendientes</option>
              <option value="aprobado">Aprobadas</option>
              <option value="rechazado">Rechazadas</option>
              <option value="">Todas</option>
            </select>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando solicitudes...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Empleado</th>
                <th>Área</th>
                <th>Tipo</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Días</th>
                <th>Motivo</th>
                <th>Soporte</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="ausencias.length === 0">
                <td colspan="10" class="text-center text-muted py-4">No hay solicitudes para este filtro.</td>
              </tr>
              <tr v-for="a in ausencias" :key="a.id">
                <td>{{ a.nombres }} {{ a.apellidos }}</td>
                <td>{{ a.area || "—" }}</td>
                <td>{{ LABELS_TIPO[a.tipo] || a.tipo }}</td>
                <td>{{ formatDate(a.fechaInicio) }}</td>
                <td>{{ formatDate(a.fechaFin) }}</td>
                <td>{{ a.diasSolicitados }}</td>
                <td>{{ a.motivo || "—" }}</td>
                <td>
                  <button
                    v-if="a.soporteDocumentoId"
                    type="button"
                    class="btn btn-sm btn-outline-info mb-0"
                    title="Ver soporte adjunto"
                    @click="soporteAbierto = a"
                  >
                    <i class="bi bi-paperclip"></i> Ver
                  </button>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><span class="badge" :class="ESTADO_BADGE[a.estado]">{{ a.estado }}</span></td>
                <td>
                  <div
                    v-if="a.estado === 'pendiente' ? (hasRole('admin') || hasRole('rrhh')) : hasRole('admin')"
                    class="d-flex gap-1"
                  >
                    <button
                      v-if="a.estado !== 'aprobado'"
                      type="button"
                      class="btn btn-sm btn-outline-success"
                      title="Aprobar"
                      @click="resolver(a, 'aprobado')"
                    >
                      <i class="bi bi-check-lg"></i>
                    </button>
                    <button
                      v-if="a.estado !== 'rechazado'"
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      title="Rechazar"
                      @click="resolver(a, 'rechazado')"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <VisorSoporte :ausencia="soporteAbierto" @cerrar="soporteAbierto = null" />
  </div>
</template>
